import {
  recordedMetresSchema,
  zonedMomentSchema,
  type ZonedMoment,
} from '@der-maschinfuehrer/contracts';

const LOCAL_DATE_TIME =
  /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})T(?<hour>\d{2}):(?<minute>\d{2})$/u;

export class AmbiguousLocalTimeError extends RangeError {
  public constructor(public readonly choices: readonly ZonedMoment[]) {
    super('Místní čas nastal dvakrát. Vyber správný UTC offset.');
    this.name = new.target.name;
  }
}

export function resolvedLocalTimeZone(): string {
  const timeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timeZone === '' ? 'UTC' : timeZone;
}

/**
 * Converts a datetime-local value using the browser's current IANA zone. The
 * HTML control intentionally carries no zone, so ambiguous fall-back times are
 * returned as separate choices and spring-forward gaps are rejected.
 */
export function zonedMomentChoicesFromLocalInput(
  value: string,
  timeZone = resolvedLocalTimeZone(),
): readonly ZonedMoment[] {
  const parts = parseLocalDateTime(value);
  const nominalUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
  const formatter = zonedPartsFormatter(timeZone);

  const candidates = new Map<number, ZonedMoment>();
  // Sample both sides of any nearby transition, derive the zone's candidate
  // offsets, then accept only instants that format back to the exact submitted
  // wall-clock minute. This handles gaps and folds without depending on the
  // host process timezone or on Date's implementation-defined disambiguation.
  const offsetCandidates = new Set<number>();
  for (const dayOffset of [-2, -1, 0, 1, 2]) {
    offsetCandidates.add(offsetAt(nominalUtc + dayOffset * 86_400_000, formatter));
  }
  for (const offsetMinutes of offsetCandidates) {
    const candidateEpoch = nominalUtc - offsetMinutes * 60_000;
    if (!sameLocalParts(candidateEpoch, parts, formatter)) continue;
    const utcOffset = formatUtcOffset(offsetMinutes);
    const instant = `${value}:00.000${utcOffset}`;
    const parsed = zonedMomentSchema.safeParse({
      instant,
      time_zone: timeZone,
      utc_offset: utcOffset,
    });
    if (parsed.success) candidates.set(candidateEpoch, parsed.data);
  }
  return [...candidates.entries()]
    .sort(([left], [right]) => left - right)
    .map(([, moment]) => moment);
}

export function zonedMomentFromLocalInput(
  value: string,
  selectedUtcOffset: string | null = null,
  timeZone = resolvedLocalTimeZone(),
): ZonedMoment {
  const choices = zonedMomentChoicesFromLocalInput(value, timeZone);
  if (choices.length === 0) {
    throw new RangeError('Zadaný místní čas v této časové zóně neexistuje.');
  }
  if (selectedUtcOffset !== null) {
    const selected = choices.find((choice) => choice.utc_offset === selectedUtcOffset);
    if (selected === undefined) {
      throw new RangeError('Vybraný UTC offset neodpovídá zadanému místnímu času.');
    }
    return selected;
  }
  if (choices.length > 1) throw new AmbiguousLocalTimeError(choices);
  const only = choices[0];
  if (only === undefined) throw new RangeError('Zadaný čas nelze převést.');
  return only;
}

export function zonedMomentNow(now: Date = new Date()): ZonedMoment {
  if (Number.isNaN(now.getTime())) throw new RangeError('Aktuální čas není platný.');
  return zonedMomentFromLocalInput(localDateTimeInputValue(now));
}

export function zonedMomentToLocalInput(value: ZonedMoment): string {
  const moment = zonedMomentSchema.parse(value);
  return moment.instant.slice(0, 16);
}

export function localDateTimeInputValue(value: Date): string {
  if (Number.isNaN(value.getTime())) throw new RangeError('Čas vyžaduje platný Date.');
  return [
    String(value.getFullYear()).padStart(4, '0'),
    '-',
    String(value.getMonth() + 1).padStart(2, '0'),
    '-',
    String(value.getDate()).padStart(2, '0'),
    'T',
    String(value.getHours()).padStart(2, '0'),
    ':',
    String(value.getMinutes()).padStart(2, '0'),
  ].join('');
}

/** Exact decimal conversion; no binary floating-point rounding is involved. */
export function parseKilometresToMetres(value: string): number | null {
  const normalized = value.trim().replace(',', '.');
  if (normalized === '') return null;
  const match = /^(?<sign>-?)(?<whole>\d+)(?:\.(?<fraction>\d{1,3}))?$/u.exec(normalized);
  if (match?.groups === undefined) {
    throw new RangeError('Kilometry zadej nejvýše na tři desetinná místa.');
  }
  const sign = match.groups.sign === '-' ? -1n : 1n;
  const whole = BigInt(match.groups.whole ?? '0');
  const fraction = BigInt((match.groups.fraction ?? '').padEnd(3, '0') || '0');
  const metres = sign * (whole * 1_000n + fraction);
  const numeric = Number(metres);
  if (!Number.isSafeInteger(numeric)) throw new RangeError('Kilometráž je mimo bezpečný rozsah.');
  return recordedMetresSchema.parse(numeric);
}

export function formatMetresAsKilometres(value: number | null): string {
  if (value === null) return '';
  const metres = recordedMetresSchema.parse(value);
  const sign = metres < 0 ? '-' : '';
  const absolute = Math.abs(metres);
  const whole = Math.floor(absolute / 1_000);
  const fraction = String(absolute % 1_000)
    .padStart(3, '0')
    .replace(/0+$/u, '');
  return `${sign}${String(whole)}${fraction === '' ? '' : `.${fraction}`}`;
}

function parseLocalDateTime(value: string): {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
} {
  const match = LOCAL_DATE_TIME.exec(value);
  if (match?.groups === undefined) throw new RangeError('Čas nemá platný místní formát.');
  const parsed = {
    year: Number(match.groups.year),
    month: Number(match.groups.month),
    day: Number(match.groups.day),
    hour: Number(match.groups.hour),
    minute: Number(match.groups.minute),
  };
  if (
    parsed.month < 1 ||
    parsed.month > 12 ||
    parsed.day < 1 ||
    parsed.day > 31 ||
    parsed.hour > 23 ||
    parsed.minute > 59
  ) {
    throw new RangeError('Čas nemá platné kalendářní hodnoty.');
  }
  return parsed;
}

function zonedPartsFormatter(timeZone: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat('en-CA', {
    calendar: 'iso8601',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    numberingSystem: 'latn',
    second: '2-digit',
    timeZone,
    year: 'numeric',
  });
}

function zonedParts(value: number, formatter: Intl.DateTimeFormat) {
  const values = Object.fromEntries(
    formatter
      .formatToParts(value)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, Number(part.value)]),
  );
  return {
    year: values.year ?? Number.NaN,
    month: values.month ?? Number.NaN,
    day: values.day ?? Number.NaN,
    hour: values.hour ?? Number.NaN,
    minute: values.minute ?? Number.NaN,
    second: values.second ?? Number.NaN,
  };
}

function offsetAt(value: number, formatter: Intl.DateTimeFormat): number {
  const parts = zonedParts(value, formatter);
  const representedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  return Math.round((representedAsUtc - value) / 60_000);
}

function sameLocalParts(
  value: number,
  expected: ReturnType<typeof parseLocalDateTime>,
  formatter: Intl.DateTimeFormat,
): boolean {
  const actual = zonedParts(value, formatter);
  return (
    actual.year === expected.year &&
    actual.month === expected.month &&
    actual.day === expected.day &&
    actual.hour === expected.hour &&
    actual.minute === expected.minute
  );
}

function formatUtcOffset(offsetMinutes: number): string {
  const sign = offsetMinutes < 0 ? '-' : '+';
  const absolute = Math.abs(offsetMinutes);
  return `${sign}${String(Math.floor(absolute / 60)).padStart(2, '0')}:${String(absolute % 60).padStart(2, '0')}`;
}
